import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';

interface ICompanyExpensesReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

// Matches ClientsBilledReportDialog.tsx's own label/field convention (the gold standard for these
// report-parameter dialogs): explicit left-aligned nowrap labels with a fixed width/marginRight, and
// fields narrowed to 65% instead of stretched to the full row width.
const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', textAlign: 'left', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '65%' };

// Backs the Accounts Receivable "Company Expenses" report - ported from XL_Company_Expenses.sql,
// deployed on the report server as CompanyExpenses (see ReportService.GetCompanyExpensesReportFile
// for the confirmed BegDate|EndDate|ClientNo @Parameters shape - read directly off all 4 of the
// report's own datasets, dbo.usp_CompanyExpenses_ExpDetail/ExpSummary/HBDetail/HBSummary). Client
// No. is optional - the underlying procs treat an empty value as "all clients".
const CompanyExpensesReportDialog = (props: ICompanyExpensesReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get: getReport, response: reportResponse } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const generateReport = async () => {
        const values = getValues();
        if (!values.begDate || !values.endDate) {
            setMessage('A beginning and ending date must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const params = new URLSearchParams({ begDate: values.begDate, endDate: values.endDate });
        if (values.clientNo) {
            params.set('clientNo', values.clientNo);
        }
        await getReport(`/v1.0/reports/GetReport/CompanyExpenses?${params.toString()}`);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No expenses matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'CompanyExpenses.xlsx';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title="Employee Expenses" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="begDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Client No."
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="clientNo"
                        type="number"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default CompanyExpensesReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/CompanyExpensesReportDialog.tsx